import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { TemplateTypeSelection } from "features/templates/components";
import { TEMPLATE_TYPES } from "features/templates/constants";
import type { TemplateType } from "features/templates/types";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import { trackMetaPixel, typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

const META_PIXEL_EVENT: Record<TemplateType, string> = {
  NOTE_TEMPLATE: "ConnectionNote",
  MESSAGE_TEMPLATE: "DirectMessage",
  INMAIL_TEMPLATE: "InMail",
  VOICE_TEMPLATE: "Voice",
};

export default function SelectTemplateType({
  isOpen,
  setIsOpen,
  initialTemplateType,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialTemplateType: TemplateType;
}) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const isDesktop = useTwBreakpoint("lg");
  const [templateType, setTemplateType] =
    useState<TemplateType>(initialTemplateType);

  useEffect(() => {
    setTemplateType(initialTemplateType);
  }, [initialTemplateType]);

  function reset() {
    setTemplateType(initialTemplateType);
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Content>
        <Header title="Choose template type" />

        <Body>
          <TemplateTypeSelection
            templateType={templateType}
            setTemplateType={setTemplateType}
            allowedTypes={typedObjectKeys(TEMPLATE_TYPES)}
          />
        </Body>

        <Footer className="justify-end">
          <Button
            onClick={() => {
              trackMetaPixel("trackCustom", META_PIXEL_EVENT[templateType]);
              setIsOpen(false);
              navigate({
                pathname: `../new/${templateType.toLowerCase()}`,
                search,
              });
            }}
          >
            Next
          </Button>
        </Footer>
      </Content>
    </Component>
  );
}
